import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, ImagePlus, Loader2, MessageSquare, Eye, Warehouse, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';

const POLL_MS = 4000;

const ChatPhoto = ({ chatId, messageId }) => {
    const [src, setSrc] = useState(null);
    useEffect(() => {
        let url;
        api.get(`/leasing/chats/${chatId}/messages/${messageId}/photo`, { responseType: 'blob' })
            .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
            .catch(() => setSrc(null));
        return () => url && URL.revokeObjectURL(url);
    }, [chatId, messageId]);
    if (!src) return <div className="w-48 h-32 bg-slate-100 rounded animate-pulse" />;
    return <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="" className="max-w-[240px] max-h-60 rounded object-cover" data-testid={`chat-photo-${messageId}`} /></a>;
};

const ChatList = ({ chats, activeId, onSelect, el }) => (
    <div className="border-r border-slate-200 overflow-y-auto" data-testid="chat-list">
        {chats.map((c) => (
            <button key={c.id} onClick={() => onSelect(c)} className={`w-full text-left px-4 py-3 border-b border-slate-100 flex items-start gap-3 transition-colors ${activeId === c.id ? 'bg-[#1B7EB5]/10' : 'hover:bg-slate-50'}`} data-testid={`chat-item-${c.id}`}>
                {c.type === 'workshop' ? <Warehouse className="w-4 h-4 mt-1 text-amber-600" /> : <Building2 className="w-4 h-4 mt-1 text-[#1B7EB5]" />}
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{c.title}</p>
                    <p className="text-xs text-slate-500 truncate">{c.last_message || (el ? 'Χωρίς μηνύματα' : 'No messages')}</p>
                </div>
                {c.unread > 0 && <span className="text-[10px] bg-red-500 text-white rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid={`chat-unread-${c.id}`}>{c.unread}</span>}
            </button>
        ))}
        {!chats.length && <p className="p-4 text-sm text-slate-400">{el ? 'Δεν υπάρχουν συζητήσεις' : 'No conversations'}</p>}
    </div>
);

const Message = ({ m, mine, chatId, el }) => (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`chat-message-${m.id}`}>
        <div className={`max-w-[75%] rounded-lg px-3 py-2 ${mine ? 'bg-[#1B7EB5] text-white' : 'bg-slate-100 text-slate-800'}`}>
            {!mine && <p className="text-[11px] font-semibold opacity-70 mb-0.5">{m.sender_name}</p>}
            {m.has_photo && <ChatPhoto chatId={chatId} messageId={m.id} />}
            {m.text && <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>}
            <p className={`text-[10px] mt-1 ${mine ? 'text-white/70' : 'text-slate-400'}`}>{new Date(m.created_at).toLocaleString(el ? 'el-GR' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' })}</p>
        </div>
    </div>
);

const Composer = ({ chatId, el, onSent, initialText = '' }) => {
    const [text, setText] = useState(initialText);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        setBusy(true);
        try {
            await api.post(`/leasing/chats/${chatId}/messages`, { text });
            setText('');
            onSent();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        } finally {
            setBusy(false);
        }
    };

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const form = new FormData();
            form.append('file', file);
            await api.post(`/leasing/chats/${chatId}/photo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
            onSent();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    return (
        <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2" data-testid="chat-composer">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="chat-photo-input" />
            <Button type="button" variant="ghost" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="chat-photo-btn"><ImagePlus className="w-5 h-5" /></Button>
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={el ? 'Γράψτε μήνυμα...' : 'Type a message...'} data-testid="chat-input" />
            <Button type="submit" disabled={busy || !text.trim()} className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white" data-testid="chat-send-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
        </form>
    );
};

export default function LeasingChatPage() {
    const { language } = useLanguage();
    const el = language === 'el';
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [params] = useSearchParams();
    const [draft, setDraft] = useState(null);
    const bottomRef = useRef(null);
    const lastAtRef = useRef(null);

    const loadChats = useCallback(() => api.get('/leasing/chats').then((r) => { setData(r.data); return r.data; }).catch(() => setData({ chats: [] })), []);
    const activeRef = useRef(null);
    const loadMessages = useCallback((chat) => {
        if (!chat) return undefined;
        const after = lastAtRef.current;
        return api.get(`/leasing/chats/${chat.id}/messages`, { params: after ? { after } : {} }).then((r) => {
            if (activeRef.current !== chat.id || !r.data.length) return;
            lastAtRef.current = r.data[r.data.length - 1].created_at;
            setMessages((prev) => {
                const seen = new Set(prev.map((m) => m.id));
                return after ? [...prev, ...r.data.filter((m) => !seen.has(m.id))] : r.data;
            });
        }).catch(() => {});
    }, []);

    useEffect(() => {
        loadChats().then((d) => {
            const list = d?.chats || [];
            const type = params.get('type');
            const party = params.get('party');
            const target = type ? list.find((c) => c.type === type && c.party_id === party) : list[0];
            if (!target) {
                if (type) toast.error(el ? 'Η συζήτηση δεν βρέθηκε' : 'Conversation not found');
                if (list[0]) setActive((a) => a || list[0]);
                return;
            }
            setActive((a) => (type || !a ? target : a));
            const ref = params.get('ref');
            if (ref) setDraft({ chatId: target.id, text: `${el ? 'Σχετικά με' : 'Re'}: ${ref} — ` });
        });
    }, [loadChats, params, el]);
    useEffect(() => {
        activeRef.current = active?.id || null;
        lastAtRef.current = null;
        setMessages([]);
        loadMessages(active);
        let tick = 0;
        const t = setInterval(() => { loadMessages(active); tick += 1; if (tick % 3 === 0) loadChats(); }, POLL_MS);
        return () => clearInterval(t);
    }, [active, loadMessages, loadChats]);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

    if (!data) return <Loader2 className="w-6 h-6 animate-spin text-[#1B7EB5]" />;
    const chats = data.chats || [];

    return (
        <div className="space-y-4" data-testid="leasing-chat-page">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Chat</h1>
                <p className="text-zinc-500">{el ? 'Συζητήσεις με μισθωτές, υπευθύνους στόλου και συνεργεία' : 'Conversations with lessees, fleet managers and workshops'}</p>
            </div>
            {data.readonly && (
                <div className="flex items-center gap-2 text-sm bg-slate-100 text-slate-600 px-3 py-2 rounded-sm" data-testid="chat-readonly-banner">
                    <Eye className="w-4 h-4" />{el ? 'Ως admin βλέπετε όλες τις συζητήσεις (μόνο ανάγνωση).' : 'As admin you can view all conversations (read-only).'}
                </div>
            )}
            <div className="fleet-card grid grid-cols-1 md:grid-cols-[280px_1fr] h-[65vh] overflow-hidden p-0">
                <ChatList chats={chats} activeId={active?.id} onSelect={setActive} el={el} />
                <div className="flex flex-col min-h-0">
                    {active ? (
                        <>
                            <div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-800" data-testid="chat-title">{active.title}</div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                                {messages.map((m) => <Message key={m.id} m={m} mine={m.sender_id === user?.id} chatId={active.id} el={el} />)}
                                {!messages.length && <p className="text-center text-sm text-slate-400 mt-10"><MessageSquare className="w-6 h-6 mx-auto mb-2" />{el ? 'Ξεκινήστε τη συζήτηση' : 'Start the conversation'}</p>}
                                <div ref={bottomRef} />
                            </div>
                            {!data.readonly && <Composer key={`${active.id}-${draft?.chatId === active.id ? draft.text : ''}`} chatId={active.id} el={el} initialText={draft?.chatId === active.id ? draft.text : ''} onSent={() => { setDraft(null); loadMessages(active); loadChats(); }} />}
                        </>
                    ) : <p className="m-auto text-slate-400">{el ? 'Επιλέξτε συζήτηση' : 'Select a conversation'}</p>}
                </div>
            </div>
        </div>
    );
}
